import { useEffect, useState } from 'react';
import { getAllShortcuts, formatShortcut } from '@/hooks/useKeyboardShortcuts';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  const shortcuts = getAllShortcuts();

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
      >
        <div
          className="bg-background rounded-lg border shadow-lg max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 id="shortcuts-title" className="text-lg font-semibold">
                Keyboard Shortcuts
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Speed up your workflow with these shortcuts
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-accent"
              aria-label="Close shortcuts help"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
            <div className="space-y-6">
              {/* Draft Actions */}
              <ShortcutSection title="Draft Actions" shortcuts={shortcuts.draft} />

              {/* Chat Actions */}
              <ShortcutSection title="Chat Actions" shortcuts={shortcuts.chat} />

              {/* Navigation */}
              <ShortcutSection title="Navigation" shortcuts={shortcuts.navigation} />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-muted/30">
            <p className="text-xs text-muted-foreground">
              Press <kbd className="px-1.5 py-0.5 rounded bg-background border text-xs">?</kbd> to
              show this help again
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

interface ShortcutSectionProps {
  title: string;
  shortcuts: Array<{ key: string; description: string }>;
}

function ShortcutSection({ title, shortcuts }: ShortcutSectionProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
        {title}
      </h3>
      <div className="space-y-2">
        {shortcuts.map((shortcut, index) => (
          <div
            key={index}
            className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50"
          >
            <span className="text-sm">{shortcut.description}</span>
            <kbd className="flex items-center gap-1 px-2 py-1 rounded bg-background border text-sm font-mono">
              {formatShortcut(shortcut.key)}
            </kbd>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hook to manage keyboard shortcuts help modal
 * Automatically shows on first visit
 */
export function useKeyboardShortcutsHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenHelp, setHasSeenHelp] = useState(false);

  useEffect(() => {
    // Check if user has seen the help before
    const seen = localStorage.getItem('crewflow:shortcuts-help-seen');
    if (!seen) {
      // Show help on first visit after a short delay
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasSeenHelp(true);
        localStorage.setItem('crewflow:shortcuts-help-seen', 'true');
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      setHasSeenHelp(true);
    }
  }, []);

  return {
    isOpen,
    openHelp: () => setIsOpen(true),
    closeHelp: () => setIsOpen(false),
    hasSeenHelp,
  };
}
